import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Button,
  Paper,
  Fade,
  Tabs,
  Tab,
  IconButton,
  InputBase,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import { VideoPlayer } from "./HelpDialogItems";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { InstructionItem } from "./functions";
import CustomInputSwitch from "./CustomInputSwitch";

import TouchAppIcon from "@mui/icons-material/TouchApp";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import SchoolIcon from "@mui/icons-material/School";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

// Import all your item arrays
import {
  lessonItems,
  quizItems,
  flashcardItems,
  textLabbetItems,
  gradingItems,
  powerpointItems,
  assistentItems,
  visionItems,
  imageItems,
  lessonPlanItems,
  podcastItems,
  hpItems,
} from "./HelpDialogItems";

import {
  podcastItemsStudent,
  quizItemsStudent,
  flashcardItemsStudent,
  assistentItemsStudent,
  gradingItemsStudent,
  imageItemsStudent,
  hpItemsStudent,
} from "./HelpDialogItemsStudents";

// Import your icons
import CreateIcon from "@mui/icons-material/Create";
import BrainIcon from "@mui/icons-material/Psychology";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import MicIcon from "@mui/icons-material/Mic";
import ClipboardIcon from "@mui/icons-material/Assignment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";

const HighlightsPage = ({ user, styles, theme }) => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [role, setRole] = useState(user.accountType);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedUserType, setSelectedUserType] = useState(
    role === "teacher" ? 0 : 1,
  );

  // Define sections with their icons, colors, and corresponding items
  const sections = [
    {
      title: "Lektion",
      icon: CreateIcon,
      color: "#3B82F6",
      path: "/app/lektioner",
      teacherItems: lessonItems,
      studentItems: [],
      showForTeacher: true,
      showForStudent: false,
    },
    {
      title: "Quiz",
      icon: BrainIcon,
      color: "#9333EA",
      path: "/app/quiz",
      teacherItems: quizItems,
      studentItems: quizItemsStudent,
      showForTeacher: true,
      showForStudent: true,
    },
    {
      title: "Flashcards",
      icon: ViewAgendaIcon,
      color: "#22C55E",
      path: "/app/flashcards",
      teacherItems: flashcardItems,
      studentItems: flashcardItemsStudent,
      showForTeacher: true,
      showForStudent: true,
    },
    {
      title: "Podcast",
      icon: MicIcon,
      color: "#5760c4",
      path: "/app/podcast",
      teacherItems: podcastItems,
      studentItems: podcastItemsStudent,
      showForTeacher: true,
      showForStudent: true,
    },
    {
      title: "Bedömning",
      icon: ClipboardIcon,
      color: "#b45309",
      path: "/app/uppsats/ny",
      teacherItems: gradingItems,
      studentItems: gradingItemsStudent,
      showForTeacher: true,
      showForStudent: true,
    },
    {
      title: "Lektionsplan",
      icon: EventNoteIcon,
      color: "#0f172a",
      path: "/app/lektioner/lektionsplan",
      teacherItems: lessonPlanItems,
      studentItems: [],
      showForTeacher: true,
      showForStudent: false,
    },
    {
      title: "Assistent",
      icon: SmartToyIcon,
      color: "#EF4444",
      path: "/app/larare",
      teacherItems: assistentItems,
      studentItems: assistentItemsStudent,
      showForTeacher: true,
      showForStudent: true,
    },
    {
      title: "Prov",
      icon: HistoryEduIcon,
      color: "#a5292a",
      path: "/app/hp",
      teacherItems: hpItems,
      studentItems: hpItemsStudent,
      showForTeacher: true,
      showForStudent: false,
    },
    {
      title: "Högskoleprov",
      icon: HistoryEduIcon,
      color: "#a5292a",
      path: "/app/hogskoleprov",
      teacherItems: hpItems,
      studentItems: hpItemsStudent,
      showForTeacher: false,
      showForStudent: true,
    },
    // Add any other sections as needed
  ];

  // Filter sections based on role
  const filteredSections = sections.filter(
    (section) =>
      (role === "teacher" && section.showForTeacher) ||
      (role === "student" && section.showForStudent),
  );

  // Update the role state when the toggle changes
  const handleUserTypeChange = (index) => {
    setSelectedUserType(index);
    const newRole = index === 0 ? "teacher" : "student";
    setRole(newRole);

    // Reset to default tab when changing roles to avoid errors
    setCurrentTab(0);
  };

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Search functionality
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter highlights based on search query
  const getFilteredHighlights = (items) => {
    if (!items) return []; // Handle case when items is undefined
    if (!searchQuery) return items;

    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description &&
          item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.listItems &&
          item.listItems.some((listItem) =>
            listItem.text.toLowerCase().includes(searchQuery.toLowerCase()),
          )),
    );
  };

  return (
    <>
      <Box sx={styles.container}>
        <Box
          sx={{
            ...styles.innerContainer,
            width: { xs: "100%", md: "100%" },
            margin: "0 auto",
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              ...styles.ibStyles,
              position: "absolute",
              top: isMobile ? 54 : 20, // Move down on mobile screens
              left: isMobile ? 10 : 0,
              zIndex: 10,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <Typography
              variant="h1"
              sx={{
                ...styles.newH1,
                marginTop: "0px",
                marginBottom: "0px",
                paddingBottom: "0px",
              }}
            >
              Utforska Studera.ai
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: { xs: "100%", md: "800px" },
                mx: "auto",
                display: "none",
              }}
            >
              Här kan du se alla verktyg och funktioner som finns tillgängliga
              för dig som {role === "teacher" ? "lärare" : "elev"}.
            </Typography>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: isMobile ? 55 : 15, // Higher position on mobile
              right: isMobile ? "50%" : 10, // Center on mobile
              transform: isMobile ? "translateX(50%)" : "none", // Center on mobile
              zIndex: isMobile ? 20 : 10,
              width: isMobile ? "100%" : "auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CustomInputSwitch
              selectedIndex={selectedUserType}
              onIndexChange={handleUserTypeChange}
              options={["Lärare", "Elever"]}
              optionIcons={[<SchoolIcon />, <PersonOutlineIcon />]}
              theme={theme}
              width={isMobile ? "200px" : "250px"}
              mobileWidth="200px"
              height={isMobile ? "30px" : "38px"}
              fontSize="0.9rem"
              iconSize="1.1em"
              iconSpacing="10px"
              borderRadius="14px"
              fontWeight="500"
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              p: 0.5,
              borderRadius: 4,
              backgroundColor:
                theme.palette.mode === "dark" ? undefined : "#f9fafb",
              backgroundImage: "none",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              mb: 9,
            }}
          >
            {/* Navigation tabs */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: `repeat(auto-fill, minmax(${role === "teacher" ? "100px" : "150px"}, 1fr))`,
                  sm: `repeat(auto-fill, minmax(${role === "teacher" ? "120px" : "180px"}, 1fr))`,
                  md: `repeat(auto-fill, minmax(${role === "teacher" ? "130px" : "180px"}, 1fr))`,
                },
                gap: 0,
              }}
            >
              {filteredSections.map((section, index, filteredArray) => (
                <Paper
                  key={section.title}
                  elevation={0}
                  onClick={() => handleChangeTab(null, index + 1)}
                  sx={{
                    p: 4,
                    pt: 3,
                    pb: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    cursor: "pointer",
                    borderTopLeftRadius: index === 0 ? 16 : undefined,
                    borderBottomLeftRadius: index === 0 ? 16 : undefined,
                    borderTopRightRadius:
                      index === filteredArray.length - 1 ? 16 : undefined,
                    borderBottomRightRadius:
                      index === filteredArray.length - 1 ? 16 : undefined,
                    backgroundColor:
                      currentTab === index + 1
                        ? `${section.color}10`
                        : theme.palette.mode === "dark"
                          ? "#1d2e3d"
                          : "#fff",
                    transition: "all 0.2s ease",
                    backgroundImage: "none",
                    position: "relative", // For positioning the arrow
                    "&:hover": {
                      transform: "scale(1.05)",
                      borderColor: "transparent",
                    },
                    "&:hover .icon": {
                      transform: "scale(1.1) rotate(7deg)",
                      transition: "transform 0.3s ease",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 50,
                      height: 50,
                      borderRadius: 70,
                      bgcolor: `${section.color}10`,
                    }}
                  >
                    <section.icon
                      className="icon"
                      sx={{
                        color: section.color,
                        fontSize: 29,
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </Box>
                  <Box
                    className="text"
                    sx={{
                      fontWeight: currentTab === index + 1 ? 700 : 500,
                      fontSize: role === "teacher" ? "0.95rem" : "0.95rem",
                      textAlign: "center",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {section.title}
                  </Box>

                  {/* Arrow indicator at bottom */}
                  {currentTab === index + 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "10px solid transparent",
                        borderRight: "10px solid transparent",
                        borderTop: `10px solid ${section.color}`,
                      }}
                    />
                  )}
                </Paper>
              ))}
            </Box>
          </Box>

          {currentTab === 0 ? (
            <Box sx={{ p: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  //backgroundColor: "transparent",
                }}
              >
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <TouchAppIcon
                    sx={{
                      fontSize: 80,
                      color: theme.palette.text.secondary,
                      opacity: 0.6,
                      mb: 3,
                    }}
                  />

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      mb: 3,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Klicka på en av flikarna i menyn ovan för att visa innehåll
                  </Typography>
                </Box>
              </Paper>
            </Box>
          ) : (
            // Show selected section
            (() => {
              const selectedSection = filteredSections[currentTab - 1];

              // Safety check to ensure selectedSection exists
              if (!selectedSection) {
                return (
                  <Box sx={{ textAlign: "center", py: 10 }}>
                    <Typography
                      variant="h5"
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      Ingen kategori vald
                    </Typography>
                    <Typography>Välj en kategori från menyn ovan.</Typography>
                  </Box>
                );
              }

              const items =
                role === "teacher"
                  ? selectedSection.teacherItems || []
                  : selectedSection.studentItems || [];

              const filteredItems = getFilteredHighlights(items);

              return (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 4,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      pb: 2,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 600, fontSize: "1.5rem" }}
                    >
                      {selectedSection.title}
                    </Typography>
                  </Box>

                  {filteredItems.length > 0 ? (
                    filteredItems.map((highlight, index) => (
                      <Box
                        key={index}
                        sx={{
                          mt: 3,
                          mb: 5,
                          p: { xs: 2, sm: 4 },
                          borderRadius: 4,
                          bgcolor:
                            theme.palette.mode === "dark" ? "#1d2e3d" : "white",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                          animation: "fadeIn 0.4s ease forwards",
                          backgroundImage: "none",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                            transition: "all 0.3s ease",
                          },
                          "@keyframes fadeIn": {
                            "0%": {
                              opacity: 0,
                              transform: "translateY(10px)",
                            },
                            "100%": {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: highlight.description ? 2 : 8,
                            fontSize: { xs: "1.4rem", md: "1.7rem" },
                          }}
                        >
                          {highlight.title}
                        </Typography>

                        {highlight.description && (
                          <Typography
                            variant="subtitle1"
                            sx={{ mb: 4, color: "text.secondary" }}
                          >
                            {highlight.description}
                          </Typography>
                        )}

                        <Grid
                          container
                          spacing={3}
                          direction={index % 2 === 0 ? "row" : "row-reverse"}
                        >
                          {/* Video */}
                          <Grid item xs={12} md={6}>
                            <VideoPlayer
                              videoUrl={highlight.videoUrl}
                              poster={highlight.poster}
                              animationType="ripple"
                              autoplayOnScroll={true}
                            />
                          </Grid>

                          {/* Features list */}
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {highlight.listItems &&
                                highlight.listItems.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        fontSize: "1.1rem",
                                      }}
                                    >
                                      Funktioner
                                    </Typography>

                                    {highlight.listItems.map((item, i) => (
                                      <InstructionItem
                                        key={i}
                                        text={item.text}
                                        marginBottom={1.5}
                                        simple="true"
                                        fontWeight="400"
                                      />
                                    ))}
                                  </Box>
                                )}

                              {highlight.useCases &&
                                highlight.useCases.length > 0 && (
                                  <Box
                                    sx={{
                                      p: 2,
                                      mt: "auto",
                                      bgcolor: `${selectedSection.color}15`,
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ mb: 1, fontWeight: 600 }}
                                    >
                                      Användningsområden:
                                    </Typography>
                                    <Typography variant="body2">
                                      {highlight.useCases.join(", ")}
                                    </Typography>
                                  </Box>
                                )}
                            </Box>
                          </Grid>
                        </Grid>
                        {highlight.path && (
                          <Box
                            sx={{
                              mt: 5,
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              variant="purp"
                              endIcon={<ArrowForwardIcon />}
                              onClick={() =>
                                handleNavigate(
                                  highlight.path || selectedSection.path,
                                )
                              }
                            >
                              Prova nu
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ textAlign: "center", py: 10 }}>
                      <Typography
                        variant="h5"
                        sx={{ mb: 2, color: "text.secondary" }}
                      >
                        Inga resultat hittades
                      </Typography>
                      <Typography>
                        Försök med en annan sökning eller välj en annan
                        kategori.
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()
          )}
        </Box>
      </Box>
    </>
  );
};

export default HighlightsPage;
